import { useState, useEffect, useCallback } from 'react';
import {
  Week,
  WeekDetails,
  CloneFormData,
  CloneResponse,
} from '../types/CloneClass.type';
import {
  getAvailableWeeks,
  getWeekDetails,
  cloneClasses,
} from '../utils/apiClient';
import { toast } from 'react-toastify';

const parseDateKey = (dateKey: string): Date | null => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateKey);
  if (!match) return null;

  const date = new Date(
    Number(match[1]),
    Number(match[2]) - 1,
    Number(match[3]),
  );

  if (
    date.getFullYear() !== Number(match[1]) ||
    date.getMonth() !== Number(match[2]) - 1 ||
    date.getDate() !== Number(match[3])
  ) {
    return null;
  }

  return date;
};

const getMonday = (date: Date): Date => {
  const monday = new Date(date);
  const day = monday.getDay();
  const diff = monday.getDate() - day + (day === 0 ? -6 : 1);
  monday.setDate(diff);
  monday.setHours(0, 0, 0, 0);
  return monday;
};

const formatDate = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const getNextWeekStart = (sourceWeekStart: string): string => {
  const sourceDate = parseDateKey(sourceWeekStart);
  if (!sourceDate) return '';

  sourceDate.setDate(sourceDate.getDate() + 7);
  return formatDate(sourceDate);
};

const getCurrentWeekMonday = (): string => formatDate(getMonday(new Date()));

export const useCloneClasses = () => {
  const [weeks, setWeeks] = useState<Week[]>([]);
  const defaultSourceWeek = getCurrentWeekMonday();
  const [sourceWeekDetails, setSourceWeekDetails] =
    useState<WeekDetails | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingWeekDetails, setLoadingWeekDetails] = useState(false);
  const [cloneResult, setCloneResult] = useState<CloneResponse | null>(null);

  const [formData, setFormData] = useState<CloneFormData>({
    sourceWeekStart: '',
    targetWeekStart: getNextWeekStart(defaultSourceWeek) || defaultSourceWeek,
    cloneAll: true,
    selectedClassIds: [],
    cloneCoaches: true,
    cloneStudents: true,
  });

  // Fetch available weeks on mount
  useEffect(() => {
    fetchWeeks();
  }, []);

  const fetchWeeks = async () => {
    try {
      setLoading(true);
      const data = await getAvailableWeeks();
      setWeeks(data);
    } catch (error) {
      const errorMessage =
        error instanceof Error && 'response' in error
          ? (error as { response?: { data?: { message?: string } } }).response
              ?.data?.message
          : 'Failed to fetch available weeks';
      toast.error(errorMessage || 'Failed to fetch available weeks');
      console.error('Error fetching weeks:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch week details when source week is selected and cloneAll is false
  const fetchSourceWeekDetails = async (startDate: string) => {
    if (!startDate) return;

    try {
      setLoadingWeekDetails(true);
      const data = await getWeekDetails(startDate);
      setSourceWeekDetails(data);
    } catch (error) {
      const errorMessage =
        error instanceof Error && 'response' in error
          ? (error as { response?: { data?: { message?: string } } }).response
              ?.data?.message
          : 'Failed to fetch week details';
      toast.error(errorMessage || 'Failed to fetch week details');
      console.error('Error fetching week details:', error);
    } finally {
      setLoadingWeekDetails(false);
    }
  };

  // Update form data
  const updateFormData = (updates: Partial<CloneFormData>) => {
    setFormData((prev) => ({ ...prev, ...updates }));
  };

  // Change the source and reset the target to the first week after it.
  const setSourceWeek = useCallback((sourceWeekStart: string) => {
    const nextTargetWeek = getNextWeekStart(sourceWeekStart);
    setSourceWeekDetails(null);
    setCloneResult(null);
    setFormData((prev) => ({
      ...prev,
      sourceWeekStart,
      targetWeekStart: nextTargetWeek,
      selectedClassIds: [],
    }));
  }, []);

  // Toggle class selection
  const toggleClassSelection = (classId: number) => {
    setFormData((prev) => {
      const isSelected = prev.selectedClassIds.includes(classId);
      return {
        ...prev,
        selectedClassIds: isSelected
          ? prev.selectedClassIds.filter((id) => id !== classId)
          : [...prev.selectedClassIds, classId],
      };
    });
  };

  // Select all classes for a specific day
  const toggleDaySelection = (day: keyof WeekDetails['groupedByDay']) => {
    if (!sourceWeekDetails) return;

    const dayClasses = sourceWeekDetails.groupedByDay[day];
    const dayClassIds = dayClasses.map((c) => c.id);
    const allSelected = dayClassIds.every((id) =>
      formData.selectedClassIds.includes(id),
    );

    setFormData((prev) => ({
      ...prev,
      selectedClassIds: allSelected
        ? prev.selectedClassIds.filter((id) => !dayClassIds.includes(id))
        : Array.from(new Set([...prev.selectedClassIds, ...dayClassIds])),
    }));
  };

  // Submit clone request
  const handleClone = async (): Promise<boolean> => {
    // Validation
    if (!formData.sourceWeekStart || !formData.targetWeekStart) {
      toast.error('Please select both source and target weeks');
      return false;
    }

    if (formData.sourceWeekStart === formData.targetWeekStart) {
      toast.error('Source and target weeks cannot be the same');
      return false;
    }

    if (!formData.cloneAll && formData.selectedClassIds.length === 0) {
      toast.error('Please select at least one class to clone');
      return false;
    }

    try {
      setLoading(true);
      const payload: CloneFormData & { classIds?: number[] } = {
        sourceWeekStart: formData.sourceWeekStart,
        targetWeekStart: formData.targetWeekStart,
        cloneAll: formData.cloneAll,
        selectedClassIds: formData.selectedClassIds,
        cloneCoaches: formData.cloneCoaches,
        cloneStudents: formData.cloneStudents,
      };

      if (!formData.cloneAll) {
        payload.classIds = formData.selectedClassIds;
      }

      // Debugging lines
      //   console.log(payload as CloneFormData);
      //   return true;

      const result = await cloneClasses(payload as CloneFormData);

      setCloneResult(result);

      // Show success message with summary
      const { summary } = result;
      toast.success(
        `Successfully cloned ${summary.totalCloned} ${summary.totalCloned === 1 ? 'class' : 'classes'}` +
          (summary.coachesCloned > 0
            ? ` with ${summary.coachesCloned} coaches`
            : '') +
          (summary.studentsCloned > 0
            ? ` and ${summary.studentsCloned} students`
            : '') +
          (summary.conflictsFound > 0
            ? `. ${summary.conflictsFound} conflict(s) found.`
            : '') +
          ((summary.studentsSkipped || result.skippedStudents?.length || 0) > 0
            ? ` ${summary.studentsSkipped || result.skippedStudents?.length} student(s) skipped.`
            : ''),
        { autoClose: 5000 },
      );

      return true;
    } catch (error) {
      const errorMessage =
        error instanceof Error && 'response' in error
          ? (error as { response?: { data?: { message?: string } } }).response
              ?.data?.message
          : 'Failed to clone classes';
      toast.error(errorMessage || 'Failed to clone classes');
      console.error('Error cloning classes:', error);
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      sourceWeekStart: '',
      targetWeekStart: getNextWeekStart(getCurrentWeekMonday()),
      cloneAll: true,
      selectedClassIds: [],
      cloneCoaches: true,
      cloneStudents: true,
    });
    setSourceWeekDetails(null);
    setCloneResult(null);
  };

  return {
    weeks,
    sourceWeekDetails,
    loading,
    loadingWeekDetails,
    formData,
    cloneResult,
    fetchWeeks,
    fetchSourceWeekDetails,
    setSourceWeek,
    updateFormData,
    toggleClassSelection,
    toggleDaySelection,
    handleClone,
    resetForm,
  };
};
